'use client'

import { HTMLProps, useRef, useState } from 'react'
import clsx from 'classnames'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useLocale } from 'use-intl'
import { FiltersBar } from '@/shared/ui/filters-bar'
import { Pagination } from '@/shared/ui/pagination'

import type { IEventPreview, IEventCategory } from '@/interfaces/events'
import { getCategoryId } from '@/shared/utils'
import { EventsAsyncList } from '@/features/events-async-list'
import getEvents, { GetEventsParams } from '@/actions/getEvents'
import { localeMap } from '@/shared/utils/directus'
import style from './events-feed.module.scss'

type EventsFeedProps = HTMLProps<HTMLDivElement> & {
  categories: IEventCategory[]
  initData: { items: IEventPreview[]; page: number; limit: number; total: string | number }
}

export const EventsFeed = (props: EventsFeedProps) => {
  const { className, initData, categories } = props
  const { items, page, total, limit } = initData
  const [currentPage, setCurrentPage] = useState(page)
  const [pageSize, setPageSize] = useState(limit)
  const [totalCount, setTotalCount] = useState(total as number)
  const currentLocale = useLocale()
  const filterRef = useRef<IEventCategory | null>(null)
  const [pages, setPages] = useState<Record<number, IEventPreview[]>>({ 0: items })

  const totalPageCount = Math.ceil(totalCount / pageSize)
  const parentRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: totalPageCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 240,
    overscan: 1,
  })
  const virtualItems = virtualizer.getVirtualItems()
  const totalSize = virtualizer.getTotalSize()

  const fetchData = ({ categoryId, sort, page }: GetEventsParams) => {
    getEvents({ locale: localeMap[currentLocale], categoryId, sort, page })
      .then(({ items, total, limit }) => {
        setPageSize(limit)
        setTotalCount(total as number)
        if (page) {
          setPages(prevState => ({ ...prevState, [page - 1]: items }))
        } else {
          setPages({ 0: items })
          setCurrentPage(1)
        }
      })
      .catch(error => {
        console.error(error)
      })
  }

  const onChangeCategory = (filter: IEventCategory | null) => {
    const categoryId = getCategoryId(filter)
    const sort = filter?.sort_children_by ? [filter.sort_children_by] : undefined

    filterRef.current = filter

    fetchData({ categoryId, sort })
  }

  const onFetchPage = (page: number) => {
    const categoryId = getCategoryId(filterRef.current)
    const sort = filterRef.current?.sort_children_by ? [filterRef.current.sort_children_by] : undefined

    fetchData({ page, categoryId, sort })
  }

  return (
    <section className={clsx(style.container, 'main-padding', 'white-section', className)} ref={parentRef}>
      <FiltersBar
        activeFilter={filterRef.current}
        filters={categories}
        onFilterChange={onChangeCategory}
        withoutAll={true}
      />

      {virtualItems?.length ? (
        <ul
          style={{
            height: totalSize,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualItems.map(virtualRow => (
            <li
              key={virtualRow.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow?.start ?? 0}px)`,
              }}
            >
              <EventsAsyncList
                anchorId={`page${virtualRow.index}`}
                cards={pages[virtualRow.index]}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                onCurrentPage={setCurrentPage}
                onFetchPage={onFetchPage}
              />
            </li>
          ))}
        </ul>
      ) : (
        <EventsAsyncList
          key="initEventsAsyncList"
          anchorId={`page0`}
          cards={pages[0]}
          data-index={0}
          onCurrentPage={setCurrentPage}
        />
      )}

      <Pagination
        className={style.pagination}
        currentPage={currentPage}
        onPageChange={page => {
          location.href = `#page${page - 1}`
          setCurrentPage(page)
        }}
        pageSize={pageSize}
        totalPageCount={totalPageCount}
      />
    </section>
  )
}
